import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { entityApprovalStatus } from '@/api/entity/types';
import { _getProfileById } from '@/api/profile/queries';
import { _getUserEntities, useGetUserProfile } from '@/api/user-dashboard/queries';
import { useBackNavigation } from '@/global-hooks/use-back-navigation';
import { getDealsReturnUrl } from '@/pages/deals/offer-route-layout/deals-return';
import { DEALS_KANBAN_TITLE } from '@/pages/deals-kanban/config';
import { PagePaths } from '@/routes/page-paths';
import { useGetSidebarLinks } from '@/shared/design-system/components/Sidebar/use-get-sidebar-links';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

const DEALS_SECTION_TITLE = 'Pre-IPO';

// Routes not in sidebar that still need a page name in the header
const STANDALONE_ROUTES: Array<{
  pattern: string;
  text: string;
  goBack: boolean;
  fallback?: string;
}> = [
  { pattern: PagePaths.settings, text: 'Manage Account', goBack: true },
  { pattern: PagePaths.createEntity, text: 'New Entity Account', goBack: true },
  { pattern: PagePaths.createEntityId, text: 'New Entity Account', goBack: true },
  { pattern: PagePaths.manageEntity, text: 'Manage Entity Account', goBack: false },
  { pattern: PagePaths.manageEntityReview, text: 'Manage Entity Account', goBack: true },
  { pattern: PagePaths.manageEntityId, text: 'Manage Entity Account', goBack: true },
  { pattern: PagePaths.setupFund, text: 'Setup Fund', goBack: true },
  { pattern: PagePaths.setupFundId, text: 'Setup Fund', goBack: true },
  { pattern: PagePaths.setupFundIdEdit, text: 'Setup Fund', goBack: true },
  { pattern: PagePaths.setupFundReview, text: 'Setup Fund', goBack: true },
  { pattern: PagePaths.introducerUser, text: 'Introducer view', goBack: true },
  // Reached from the referral and provider dashboards, so it has no sidebar entry of its
  // own and would otherwise render with an empty header.
  { pattern: PagePaths.dealsPipeline, text: 'Deals Pipeline', goBack: true },
  { pattern: PagePaths.userProfile, text: 'User Profile', goBack: true },
  { pattern: PagePaths.addressBookContact, text: 'User Profile', goBack: true },
  {
    pattern: PagePaths.portfolioSend,
    text: 'Send',
    goBack: true,
    fallback: `${PagePaths.portfolio}?tab=my-assets`,
  },
  {
    pattern: PagePaths.portfolioReceive,
    text: 'Receive crypto assets',
    goBack: true,
    fallback: `${PagePaths.portfolio}?tab=my-assets`,
  },
];

const SUB_PAGE_ROUTES: Array<{ pattern: string; parentBase: string }> = [
  { pattern: PagePaths.allFundsId, parentBase: PagePaths.allFunds },
  { pattern: PagePaths.allFundsFeeder, parentBase: PagePaths.allFunds },

  { pattern: PagePaths.fundDashboardId, parentBase: PagePaths.fundDashboard },
  { pattern: PagePaths.overviewEntity, parentBase: PagePaths.overview },
  { pattern: PagePaths.overviewFund, parentBase: PagePaths.overview },

  { pattern: PagePaths.dealsOfferView, parentBase: PagePaths.deals },
  { pattern: PagePaths.dealsCompanyProfile, parentBase: PagePaths.deals },
  { pattern: PagePaths.dealsCreateCompany, parentBase: PagePaths.deals },
  { pattern: PagePaths.dealsEditCompany, parentBase: PagePaths.deals },
  { pattern: PagePaths.dealsPostOffer, parentBase: PagePaths.deals },

  { pattern: PagePaths.addressBookCategories, parentBase: PagePaths.addressBook },
  { pattern: PagePaths.addressBookCategory, parentBase: PagePaths.addressBook },
  { pattern: PagePaths.addressBookEntity, parentBase: PagePaths.addressBook },
];

interface ExactPageNameInput {
  pathname: string;
  defaultPageName: string;
  isIntroducerAdmin: boolean;
  isDealsKanban: boolean;
  isEntityContext?: boolean;
  receiveSymbol?: string;
}

export const resolveExactPageName = ({
  pathname,
  defaultPageName,
  isIntroducerAdmin,
  isDealsKanban,
  isEntityContext,
  receiveSymbol,
}: ExactPageNameInput) => {
  if (pathname === PagePaths.portfolio) {
    return isEntityContext ? 'Company Portfolio' : 'My Portfolio';
  }
  if (pathname === PagePaths.portfolioSend) return 'Send';
  if (pathname === PagePaths.portfolioReceive) {
    return receiveSymbol ? `Receive ${receiveSymbol}` : 'Receive crypto assets';
  }
  if (isIntroducerAdmin) return 'Introducer Program: Admin Dashboard';
  if (isDealsKanban) return DEALS_KANBAN_TITLE;
  if (pathname === PagePaths.deals) return DEALS_SECTION_TITLE;
  return defaultPageName;
};

export const useHeaderPage = () => {
  const location = useLocation();
  const { pathname, state } = location;
  const routeState =
    (state as {
      initialCoinSymbol?: string;
      selectedChainId?: string;
      returnTo?: string;
      returnState?: unknown;
    } | null) ?? null;
  const receiveSymbol = routeState?.initialCoinSymbol;
  const navigate = useNavigate();
  const { selectedEntityId, selectedUserRole } = useSelectedUserConfigSlice();
  const { user } = useGetUserProfile();
  const { data: entitiesData } = useQuery(_getUserEntities());
  const selectedEntity =
    selectedUserRole === 'entity'
      ? entitiesData?.entities.find((entity) => entity.entityId === selectedEntityId)
      : undefined;
  const isEntityRejected =
    selectedEntity?.entityApprovalStatus === entityApprovalStatus.Rejected ||
    selectedEntity?.entityApprovalStatus === entityApprovalStatus.Blocked;

  const introducerUserMatch =
    pathname === PagePaths.introducerHowToEarn
      ? null
      : matchPath(PagePaths.introducerUser, pathname);
  const introducerUserId = introducerUserMatch?.params?.id ?? undefined;
  const { data: introducerProfile } = useQuery(_getProfileById(introducerUserId));

  const links = useGetSidebarLinks({
    role: selectedUserRole,
    entityId: selectedEntityId || undefined,
    userId: user?.id,
    hasSubmittedQuestionnaire: user?.hasSubmittedQuestionnaire,
    isBlocked: user?.isBlocked,
    isEntityRejected,
    kycStatus: user?.kycStatus,
    isNonAccredited: user?.isNonAccredited,
  });

  const flatLinks = useMemo(() => links.flat(), [links]);

  const isEntityContext = selectedUserRole === 'entity';

  const { pageName, isSubPage, parentHref, isTopLevel } = useMemo(() => {
    const exactLink = flatLinks.find((link) => link.href?.split('?')[0] === pathname);
    if (exactLink) {
      const isIntroducerAdmin = pathname === PagePaths.introducer && selectedUserRole === 'admin';
      // The kanban board's sidebar entry is "Pipeline" but the page header title differs.
      const isDealsKanban = pathname === PagePaths.dealsKanban;
      const text = resolveExactPageName({
        pathname,
        defaultPageName: exactLink.text,
        isIntroducerAdmin,
        isDealsKanban,
        isEntityContext,
        receiveSymbol,
      });
      return { pageName: text, isSubPage: false, parentHref: null, isTopLevel: true };
    }

    // `/all-funds/:identifier/invest` also matches the feeder-fund pattern, which would name
    // the subscription wizard after the funds list. The wizard draws its own header, as the
    // event wizard does, so this route carries no app-chrome title at all.
    const subPageEntry = matchPath(PagePaths.allFundsInvest, pathname)
      ? undefined
      : SUB_PAGE_ROUTES.find(({ pattern }) => matchPath(pattern, pathname));
    if (subPageEntry) {
      const parentLink = flatLinks.find(
        (link) => link.href?.split('?')[0] === subPageEntry.parentBase,
      );
      let text =
        subPageEntry.parentBase === PagePaths.deals
          ? DEALS_SECTION_TITLE
          : (parentLink?.text ?? null);
      // `/deals/:id` precedes `/deals/create-company` and `/deals/post-offer` in SUB_PAGE_ROUTES
      // and matches them first, so the winning entry cannot identify the page — test the pathname.
      if (matchPath(PagePaths.dealsPostOffer, pathname)) {
        text = 'Post New Offer';
      } else if (matchPath(PagePaths.dealsCreateCompany, pathname)) {
        text = 'Create New Company';
      } else if (matchPath(PagePaths.dealsEditCompany, pathname)) {
        text = 'Edit Company';
      } else if (subPageEntry.pattern === PagePaths.dealsCompanyProfile) {
        text = 'Company details';
      } else if (subPageEntry.pattern === PagePaths.dealsOfferView) {
        // The offer panel sits over the Pre-IPO list it was opened from, else over the company page.
        text = getDealsReturnUrl(state) ? DEALS_SECTION_TITLE : 'Company details';
      }
      return {
        pageName: text,
        isSubPage: true,
        parentHref: parentLink?.href ?? subPageEntry.parentBase,
        isTopLevel: false,
      };
    }

    const standaloneEntry = STANDALONE_ROUTES.find(({ pattern }) => matchPath(pattern, pathname));
    if (standaloneEntry) {
      let text = resolveExactPageName({
        pathname,
        defaultPageName: standaloneEntry.text,
        isIntroducerAdmin: false,
        isDealsKanban: false,
        isEntityContext,
        receiveSymbol,
      });
      if (standaloneEntry.pattern === PagePaths.introducerUser && introducerProfile?.name) {
        text = `Introducer view: ${introducerProfile.name}`;
      }
      return {
        pageName: text,
        isSubPage: standaloneEntry.goBack,
        parentHref: standaloneEntry.fallback ?? null,
        isTopLevel: false,
      };
    }

    return { pageName: null, isSubPage: false, parentHref: null, isTopLevel: false };
  }, [
    pathname,
    state,
    flatLinks,
    selectedUserRole,
    isEntityContext,
    introducerProfile,
    receiveSymbol,
  ]);

  const defaultOnBack = useBackNavigation(parentHref || PagePaths.allFunds);

  // A page can be entered with a `returnTo`/`returnState` pair in router state (e.g. a modal
  // that navigated away to a full page) so the back arrow restores that origin instead of
  // going to the section's generic parent.
  const { returnTo, returnState } = routeState ?? {};

  const onBack = useCallback(() => {
    if (pathname === PagePaths.portfolioReceive && routeState?.selectedChainId) {
      const networkSelectionState = { ...routeState };
      delete networkSelectionState.selectedChainId;
      navigate(PagePaths.portfolioReceive, { state: networkSelectionState, replace: true });
      return;
    }
    if (returnTo) {
      navigate(returnTo, { state: returnState ?? null, replace: true });
      return;
    }
    defaultOnBack();
  }, [pathname, routeState, returnTo, returnState, navigate, defaultOnBack]);

  // The mobile Offer Details screen renders its own header, whose back restores the offer card.
  const hiddenOnMobile = !!matchPath(PagePaths.dealsOfferView, pathname);
  // On a phone the Pre-IPO and Gamma Team tabs open the screen, like the mobile app's tab bar.
  const titleHiddenOnMobile =
    pathname === PagePaths.deals || pathname === PagePaths.gammaTeamManagement;

  return {
    pageName,
    showBackButton: isSubPage,
    isTopLevel,
    onBack,
    hiddenOnMobile,
    titleHiddenOnMobile,
  };
};
